import axios from 'axios';

import { PasswordChangeError } from '~/errors/PasswordChangeError';
import { TPSServerError } from '~/errors/TPSServerError';
import type { ChangePasswordRequest, CredentialsRequest, LoginRequest, LoginResponse } from '~/types/api-types';

export async function sendTwoFA(args: CredentialsRequest) {
  try {
    return await axios.post('/api/authentication/sendtwofa', args, {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (error: any) {
    console.error(error.message);
    throw new Error(`Misslyckandes att skicka QR kod: ${error.message}`);
  }
}

export async function credentialsCheck(args: CredentialsRequest) {
  try {
    return await axios.post('/api/authentication/credentials', args, {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (error: any) {
    if (error instanceof TPSServerError) {
      switch (error.errorCode) {
        case 105:
          throw new Error('Ditt konto är låst. Vänligen återställ ditt lösenord för att kunna logga in.');
        case 103:
          throw new Error('Felaktigt användarnamn eller lösenord');
        case 114:
          throw new Error('Ditt lösenord har löpt ut. Vänligen återställ det för att kunna logga in.');
        default:
          throw new Error(`Kunde inte logga in: ${error.message}`);
      }
    } else {
      console.error(error.message);
      throw new Error(`Kunde inte logga in på grund av okänt fel: ${error.message}`);
    }
  }
}

export async function login(args: LoginRequest) {
  try {
    const response = await axios.post('/api/authentication/login', args, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data as LoginResponse;
  } catch (error: any) {
    if (error instanceof TPSServerError) {
      switch (error.errorCode) {
        case 105:
          throw new Error('Ditt konto är låst. Vänligen återställ ditt lösenord för att kunna logga in.');
        case 103:
          throw new Error('Felaktig kod');
        case 114:
          throw new Error('Ditt lösenord har löpt ut. Vänligen återställ det för att kunna logga in.');
        default:
          throw new Error(`Kunde inte logga in: ${error.message}`);
      }
    } else {
      console.error(error.message);
      throw new Error(`Kunde inte logga in på grund av okänt fel: ${error.message}`);
    }
  }
}

export async function refresh() {
  try {
    const response = await axios.patch('/api/authentication/refresh');
    return response.data as LoginResponse;
  } catch (error: any) {
    // Nothing user can do to react to this
    if (error instanceof TPSServerError) {
      switch (error.errorCode) {
        case 109:
          console.warn('Failed to refresh token, server could not find user.');
          return null;
        default:
          break;
      }
    }
    console.warn(`Failed to refresh login token: ${error.message}`);
  }
  return null;
}

export async function validate() {
  try {
    await axios.get('/api/authentication/validate');
    return true;
  } catch (error: any) {
    console.warn(error.message);
    return false;
  }
}

export async function changePass(args: ChangePasswordRequest) {
  try {
    await axios.put(
      `/api/account/${args.userId}/password`,
      { oldPassword: args.oldPassword, newPassword: args.newPassword },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return;
  } catch (error: any) {
    if (error instanceof TPSServerError) {
      switch (error.errorCode) {
        case 104:
          throw new PasswordChangeError(
            `Det nya lösenordet uppfyller inte lösenordskraven (${error.data.errors.join(', ')})`,
            'new'
          );
        case 113:
          throw new PasswordChangeError('Du får inte återanvända ett gammalt lösenord.', 'new');
        case 111:
          throw new PasswordChangeError('Du har angivit ett felaktigt lösenord.', 'old');
        case 110:
          throw new PasswordChangeError('Du måste ange ditt nuvarande lösenord.', 'old');
        case 109:
          throw new PasswordChangeError('Användaren finns inte.', 'none');
        case 108:
          throw new PasswordChangeError(
            'Du måste vara administratör för att ändra en annan användares lösenord.',
            'none'
          );
        default:
          throw new PasswordChangeError(`Oväntat serverfel: ${error.message}`, 'none');
      }
    } else {
      throw new Error(`Oväntat fel: ${error.message}`);
    }
  }
}

export async function resetPass(username: string) {
  try {
    await axios.post(
      `/api/account/${username}/reset`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return;
  } catch (error: any) {
    if (error instanceof TPSServerError) {
      throw new PasswordChangeError(`Oväntat serverfel: ${error.message}`, 'none');
    } else {
      throw new Error(`Oväntat fel: ${error.message}`);
    }
  }
}
